import {
  DateInputRenderer,
  getRendererProps,
  RadioSelectionRenderer,
  type RadioSelectionRendererProps,
  type DateInputRendererProps,
  SearchSelectionRenderer,
  type SearchSelectionRendererProps,
  TextareaRenderer,
  type TextareaRendererProps,
} from '@exam-center/components';
import { Box, Flex, FormDateInput, SectionTitle, Text } from '@shared/components';

import {
  BlockedCoursesSelectionField,
  type BlockedCoursesSelectionFieldProps,
} from '../blocked-courses-selection-field';
import {
  BlockedEndDateRenderer,
  type BlockedEndDateRendererProps,
} from '../blocked-end-date-renderer';

export interface BlocklistProctorInformationFieldsProps {
  fields: {
    startedAt: DateInputRendererProps;
    endedAt: BlockedEndDateRendererProps;
    blockedCourseType: RadioSelectionRendererProps;
    remark: TextareaRendererProps;
    examCenter?: SearchSelectionRendererProps;
    blockedCourses?: BlockedCoursesSelectionFieldProps;
  };
  options?: {
    shouldDisplayEndedAtAsDate?: boolean;
    shouldDisplayBlockedCourseSelection?: boolean;
    shouldDisplayExamCenterSelection?: boolean;
    blockedUntilMode?: 'duration' | 'date';
  };
  slots?: {
    endedAtDateEndAdornment?: React.ReactNode;
    blockedAtLabelText?: string;
    blockedUntilLabelText?: string;
    blockedUntilUnassignedLabelText?: string;
    blockedUntilAssignedLabelText?: string;
  };
}

// TODO: rename this to be more generic name since it is used in blocked cheater as well
export default function BlocklistProctorInformationFields(
  props: Readonly<BlocklistProctorInformationFieldsProps>,
) {
  const { fields, options, slots } = props;

  const { blockedUntilMode = 'duration', ...restOptions } = options ?? {};

  const isBlockedUntilDurationMode = blockedUntilMode === 'duration';

  const blockedAtLabel = slots?.blockedAtLabelText ?? 'วันที่เริ่มปฏิบัติหน้าที่';
  const blockedUntilLabel = slots?.blockedUntilLabelText ?? 'วันที่สิ้นสุดปฏิบัติหน้าที่';

  return (
    <Flex
      sx={(theme) => ({
        gap: theme.spacing(6),
        p: theme.spacing(6),
        flexDirection: 'column',
      })}
    >
      <SectionTitle title='Blocklist Information' />
      <Flex sx={(theme) => ({ gap: theme.spacing(6), flex: 1 })}>
        <Text variant='body1Semibold' sx={{ flexBasis: '240px' }}>
          {blockedAtLabel}
        </Text>
        <DateInputRenderer
          {...getRendererProps(fields.startedAt, {
            label: blockedAtLabel,
            sx: { width: '212px' },
          })}
        />
      </Flex>
      <Flex sx={(theme) => ({ gap: theme.spacing(6), flex: 1 })}>
        <Text variant='body1Semibold' sx={{ flexBasis: '240px' }}>
          {blockedUntilLabel}
        </Text>
        {!isBlockedUntilDurationMode && fields.endedAt.mode === 'input' ? (
          <Flex sx={(theme) => ({ gap: theme.spacing(4), flex: 1 })}>
            <Box sx={{ flexBasis: '212px' }}>
              <FormDateInput
                name={fields.endedAt.date.name}
                required={fields.endedAt.date.required}
                label={blockedUntilLabel}
                placeholder='DD-MM-YYYY'
                inputFormat='DD-MM-YYYY'
                minDate={new Date()}
              />
            </Box>
            {slots?.endedAtDateEndAdornment}
          </Flex>
        ) : (
          <BlockedEndDateRenderer
            {...getRendererProps(fields.endedAt, {
              label: blockedUntilLabel,
              unassignedLabel: slots?.blockedUntilUnassignedLabelText,
              assignedLabel: slots?.blockedUntilAssignedLabelText,
              sx: {
                width: '212px',
                display: restOptions.shouldDisplayEndedAtAsDate ? 'block' : 'none',
              },
              endAdornment: slots?.endedAtDateEndAdornment,
            })}
          />
        )}
      </Flex>
      <Flex sx={(theme) => ({ gap: theme.spacing(6), flex: 1 })}>
        <Text variant='body1Semibold' sx={{ flexBasis: '240px' }}>
          หลักสูตรที่ระงับสิทธิ์สอบ
        </Text>
        <Flex sx={{ flexDirection: 'column', flex: '600px' }}>
          <RadioSelectionRenderer {...fields.blockedCourseType} />
          {restOptions.shouldDisplayBlockedCourseSelection && fields.blockedCourses ? (
            <BlockedCoursesSelectionField {...fields.blockedCourses} />
          ) : null}
        </Flex>
      </Flex>
      {restOptions.shouldDisplayExamCenterSelection && fields.examCenter ? (
        <Flex sx={(theme) => ({ gap: theme.spacing(6), flex: 1 })}>
          <Text variant='body1Semibold' sx={{ flexBasis: '240px' }}>
            ศูนย์ทดสอบ
          </Text>
          <Box sx={{ flexBasis: '600px' }}>
            <SearchSelectionRenderer
              {...getRendererProps(fields.examCenter, {
                label: 'ศูนย์ทดสอบ',
                placeholder: 'เลือกศูนย์ทดสอบ',
              })}
            />
          </Box>
        </Flex>
      ) : null}
      <Flex sx={(theme) => ({ gap: theme.spacing(6), flex: 1 })}>
        <Text variant='body1Semibold' sx={{ flexBasis: '240px' }}>
          หมายเหตุ
        </Text>
        <Box sx={{ flexBasis: '600px' }}>
          <TextareaRenderer
            {...getRendererProps(fields.remark, {
              label: 'หมายเหตุเพิ่มเติม',
              placeholder: 'ระบุความคิดเห็นเพิ่มเติม',
            })}
          />
        </Box>
      </Flex>
    </Flex>
  );
}
